// Pure presentation helpers for the Ownership & Accountability dashboard. No React here.
// Money/percentage/tone primitives are shared with the other cost-control tabs via ../money.
import dayjs from "dayjs";
import { humanEUR, pct, statusTone, worstTone } from "../money";

export { humanEUR, pct, statusTone, worstTone };

// currentPeriod returns the current YYYY-MM in LOCAL time. We deliberately match Financial
// Control's currentPeriod() (also local) rather than UTC: both dashboards live in the same nav
// cluster, and a UTC/local split made them disagree on "this month" near a month boundary. The
// page always sends an explicit period, so the backend's UTC default never comes into play.
export function currentPeriod() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}`;
}

// formatPeriodLabel turns "2026-06" into a human label "June 2026" for display.
export function formatPeriodLabel(period) {
  const d = dayjs(`${period}-01`);
  return d.isValid() ? d.format("MMMM YYYY") : period;
}

// monthToDate reports whether the selected period is the current, still-accruing month so the UI
// can warn that its figures are partial. Returns {isCurrent, day, daysInMonth} for the current
// month; {isCurrent:false} otherwise.
export function monthToDate(period) {
  if (period !== currentPeriod()) return { isCurrent: false };
  const now = dayjs();
  return { isCurrent: true, day: now.date(), daysInMonth: now.daysInMonth() };
}

// unassignedSeverity scales the urgency of unowned spend to its share of total spend, so €1 out of
// €260k reads calm and a 15% gap reads critical. Returns a UI tone key. This is the SINGLE source
// of unassigned severity — the verdict banner, the Unassigned KPI tile, and the remediation strip
// all derive their colour from it, so the same fact never shows two contradicting colours.
//   <€1 → neutral (effectively "owned") · <2% → ok (immaterial) · 2–10% → attention · ≥10% → critical
export function unassignedSeverity(cost, p) {
  if (Math.round(Number(cost) || 0) < 1) return "neutral";
  const frac = Number(p) || 0;
  if (frac >= 0.1) return "critical";
  if (frac >= 0.02) return "attention";
  return "ok";
}

// buildOwnerTree turns the flat owners[] (each carrying parentNodeId) into a nested tree.
// Roots are owners whose parent is null OR whose parent isn't present in the list (so a
// mid-tree slice still renders). Order within a level follows the input order.
export function buildOwnerTree(owners = []) {
  const byId = new Map();
  owners.forEach((o) => byId.set(o.ownerNodeId, { ...o, children: [] }));
  const roots = [];
  owners.forEach((o) => {
    const node = byId.get(o.ownerNodeId);
    const parent = o.parentNodeId != null ? byId.get(o.parentNodeId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  return roots;
}

// hasOwnerProblem flags an owner that warrants attention, powering the "problems only" filter.
// On the accountability lens, an owner is a problem when it is at/over budget (the outcome it is
// answerable for) or its spend is rising sharply. Forecast accuracy is a predictability concern
// owned by the Forecast tab, so it is deliberately NOT part of this signal.
export function hasOwnerProblem(o = {}) {
  const b = o.budget;
  const overBudget = !!b && (b.status === "amber" || b.status === "red");
  const rising = !!o.trend && Number(o.trend.deltaPct) > 0.1;
  return overBudget || rising;
}

// budgetChip maps an owner.budget object to a {label, tone}. null budget → neutral "no budget".
// The budget is the owner's GOVERNING budget (often a parent's), so when it's named we show the
// name + adherence ("Eng FY26 · 82%") to make clear which budget the % refers to.
export function budgetChip(budget) {
  if (!budget) return { label: "no budget", tone: "neutral" };
  const p = pct(budget.pctOfCap);
  // Always spell out "of cap" so the % is unambiguous (it's adherence to the cap, not a share of
  // total spend). The budget name (often a parent/department budget) gives the governing scope.
  const label = budget.budgetName ? `${budget.budgetName} · ${p} of cap` : `${p} of cap`;
  return { label, tone: statusTone(budget.status) };
}

